import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Blocks, Plus } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { MODULI_MENU, MODULI_PRATICA } from "../lib/moduli";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export function Group({ title, items, off, toggle, locked }) {
  return (
    <div>
      <div className="mb-2 text-[11px] uppercase tracking-wider text-slate-400">{title}</div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
        {items.map((m) => {
          const lk = locked?.has(m.key);
          const isOff = lk || off.has(m.key);
          return (
            <label key={m.key} className="flex items-center justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-slate-800/50 text-sm">
              <span className={isOff ? "text-slate-500 line-through" : "text-slate-100"}>{m.label}{lk && <span className="ml-1.5 text-[10px] no-underline text-amber-400/80" data-testid={`modulo-locked-${m.key}`}>dal piano</span>}</span>
              <Switch checked={!isOff} disabled={lk} onCheckedChange={() => toggle(m.key)} data-testid={`modulo-switch-${m.key}`} className="data-[state=checked]:bg-emerald-500" />
            </label>
          );
        })}
      </div>
    </div>
  );
}

// Superadmin: moduli predefiniti per piano di abbonamento (applicati ai nuovi studi con quel piano).
export function ModuliPianiDialog({ onClose }) {
  const [piani, setPiani] = useState(null);
  const [inUso, setInUso] = useState([]);
  const [sel, setSel] = useState(0);
  const [nuovo, setNuovo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/admin/moduli-piani").then(({ data }) => {
      const list = data.piani || [];
      (data.piani_in_uso || []).forEach((n) => { if (!list.some((p) => p.nome.toLowerCase() === n.toLowerCase())) list.push({ nome: n, disattivati: [] }); });
      setPiani(list); setInUso(data.piani_in_uso || []);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  const cur = piani?.[sel];
  const off = new Set(cur?.disattivati || []);
  const toggle = (k) => setPiani((ps) => ps.map((p, i) => (i !== sel ? p : { ...p, disattivati: off.has(k) ? p.disattivati.filter((x) => x !== k) : [...p.disattivati, k] })));
  const add = () => {
    const n = nuovo.trim();
    if (!n) return;
    if (piani.some((p) => p.nome.toLowerCase() === n.toLowerCase())) { toast.error("Piano già presente"); return; }
    setPiani([...piani, { nome: n, disattivati: [] }]); setSel(piani.length); setNuovo("");
  };
  const remove = (i) => { setPiani(piani.filter((_, j) => j !== i)); setSel(0); };
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/moduli-piani", { piani }); toast.success("Moduli per piano salvati"); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="moduli-piani-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Blocks className="h-4 w-4 text-amber-400" />Moduli per piano</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-400 -mt-2">I moduli spenti nel piano spariscono per tutti gli studi con quel piano (anche quelli già esistenti) e non si possono riaccendere dal pulsante "Moduli" dello studio, dove compaiono come "dal piano".</p>
        {!piani ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <div className="grid md:grid-cols-[max-content_1fr] gap-5">
            <div className="space-y-1">
              {piani.map((p, i) => (
                <div key={p.nome} className={`flex items-center gap-1 rounded-md ${i === sel ? "bg-amber-500/15 text-amber-300" : "hover:bg-slate-800/60"}`}>
                  <button type="button" onClick={() => setSel(i)} data-testid={`piano-${i}`} className="flex-1 text-left px-2 py-1.5 text-sm whitespace-nowrap">{p.nome}<span className="ml-1 text-[10px] text-slate-500">{p.disattivati.length ? `${p.disattivati.length} off` : "tutto on"}</span></button>
                  {!inUso.some((n) => n.toLowerCase() === p.nome.toLowerCase()) && <button type="button" onClick={() => remove(i)} title="Rimuovi piano" data-testid={`piano-remove-${i}`} className="px-1.5 text-slate-500 hover:text-red-400">×</button>}
                </div>
              ))}
              <div className="flex gap-1 pt-2">
                <Input value={nuovo} onChange={(e) => setNuovo(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="Nuovo piano" data-testid="piano-new-input" className="h-8 w-28 bg-slate-900/60 border-slate-700" />
                <Button type="button" onClick={add} data-testid="piano-new-add" className="h-8 px-2 bg-slate-700 hover:bg-slate-600"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
            {cur ? (
              <div className="space-y-5">
                <Group title={`Menu laterale · ${cur.nome}`} items={MODULI_MENU} off={off} toggle={toggle} />
                <Group title="Pulsanti nella pratica" items={MODULI_PRATICA} off={off} toggle={toggle} />
              </div>
            ) : <div className="text-sm text-slate-400">Aggiungi un piano per impostarne i moduli.</div>}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="moduli-piani-cancel">Annulla</Button>
          <Button onClick={save} disabled={busy || !piani} data-testid="moduli-piani-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Superadmin: attiva/disattiva i moduli del gestionale per un singolo studio.
export function ModuliStudioDialog({ tenant, onClose }) {
  const [off, setOff] = useState(null);
  const [locked, setLocked] = useState(new Set());
  const [piano, setPiano] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/admin/tenants/${tenant.id}/moduli`).then(({ data }) => { setOff(new Set(data.disattivati || [])); setLocked(new Set(data.da_piano || [])); setPiano(data.piano || ""); }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [tenant.id]);
  const toggle = (k) => setOff((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/tenants/${tenant.id}/moduli`, { disattivati: [...off] });
      toast.success("Moduli aggiornati"); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="bg-[#121824] border-slate-700 text-slate-100 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="moduli-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Blocks className="h-4 w-4 text-amber-400" />Gestione Moduli · {tenant.studio_name || tenant.name}</DialogTitle></DialogHeader>
        {!off ? <div className="py-8 flex justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div> : (
          <div className="space-y-5">
            <p className="text-xs text-slate-400">I moduli spenti spariscono per tutti gli utenti dello studio. Dashboard, Gestione Pratiche, Anagrafiche e Impostazioni restano sempre attivi.{locked.size > 0 && <> I moduli segnati "dal piano" sono disattivati dal piano <b className="text-amber-300">{piano}</b>: per riattivarli modifica il piano in "Moduli per piano" o cambia piano allo studio.</>}</p>
            <Group title="Menu laterale" items={MODULI_MENU} off={off} toggle={toggle} locked={locked} />
            <Group title="Pulsanti nella pratica" items={MODULI_PRATICA} off={off} toggle={toggle} locked={locked} />
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="moduli-cancel">Annulla</Button>
          <Button onClick={save} disabled={busy || !off} data-testid="moduli-save" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
